import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "styled-components";
import styled from "styled-components";

import { SelectBox, Option } from "@monolith-forensics/monolith-ui";
import LocationsAPI from "@/api/locations";
import type { Location } from "@/pages/Locations/LocationsSection";

export interface LocationOption {
  location_id: number;
  location_name: string;
}

interface LocationCocSelectorProps {
  label?: string;
  required?: boolean;
  placeholder?: string;
  description?: string;
  defaultValue?: number;
  value?: number;
  disabled?: boolean;
}

// Tooltip content component
const TooltipContent = styled(
  ({ className, data }: { className?: string; data: Location }) => {
    return (
      <div className={className}>
        <div className="detail-title">Location Details</div>
        <div className="detail-item">
          <div className="detail-label">Type: </div>
          <div className="detail-value">{data.location_type || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Office: </div>
          <div className="detail-value">{data.office_name || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Group: </div>
          <div className="detail-value">{data.location_group || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Evidence Count: </div>
          <div className="detail-value">{data.evidence_count || 0}</div>
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
    margin-bottom: 8px;
  }

  .detail-item {
    display: flex;
    gap: 2px;
  }

  .detail-label {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 12px;
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 12px;
  }
`;

const LocationContent: React.FC<{ location: Location }> = ({ location }) => {
  const theme = useTheme() as { palette: { text: { secondary: string } } };

  const locationPath = location.location_group
    ? `${location.office_name}/${location.location_group}`
    : location.office_name;

  return (
    <div
      style={{
        fontSize: "0.9em",
        marginTop: "4px",
        color: theme.palette.text.secondary,
        lineHeight: "1.2",
        display: "-webkit-box",
        WebkitLineClamp: 1,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {locationPath}
    </div>
  );
};

const LocationCocSelector: React.FC<LocationCocSelectorProps> = ({
  label = "Received By",
  required = false,
  placeholder = "Select location",
  ...restProps
}) => {
  const { data: locations } = useQuery({
    queryKey: ["evidence:locations", { include_groups: false }],
    queryFn: () => LocationsAPI.getLocations({}, { include_groups: false }),
  });

  // Transform locations for SelectBox
  const locationOptions =
    locations?.map((location: Location) => {
      const locationPath = location.location_group
        ? `${location.office_name}/${location.location_group}`
        : location.office_name;

      // Include both location name and path for searchability
      const searchableLabel = `${location.location_name} (${locationPath})`;

      return {
        label: searchableLabel,
        value: location,
        data: location,
      };
    }) || [];

  return (
    <SelectBox
      label={label}
      required={required}
      placeholder={placeholder}
      data={locationOptions}
      searchable
      openOnFocus={false}
      OptionTooltip={TooltipContent}
      renderOption={(item: string | Option) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return (
          <div style={{ padding: "5px 0" }}>
            <div>{item.data?.location_name || item.label}</div>
            {item.data && <LocationContent location={item.data} />}
          </div>
        );
      }}
      {...restProps}
    />
  );
};

export default LocationCocSelector;
